import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  TrendingUp, ArrowUpRight, Loader2, Filter, Trophy,
  Sparkles, Info, ChevronRight, Home, Building2,
} from 'lucide-react';
import { api, AED_short } from '../lib/api';
import { Link } from 'react-router-dom';

const BAND_STYLES = {
  exceptional: { bg: 'from-emerald-500 to-emerald-600', pill: 'bg-emerald-500/95 text-white', label: 'Exceptional' },
  high:        { bg: 'from-teal-500 to-teal-600', pill: 'bg-teal-500/95 text-white', label: 'High' },
  healthy:     { bg: 'from-sky-500 to-sky-600', pill: 'bg-sky-500/95 text-white', label: 'Healthy' },
  moderate:    { bg: 'from-amber-500 to-amber-600', pill: 'bg-amber-500/95 text-white', label: 'Moderate' },
  low:         { bg: 'from-orange-500 to-orange-600', pill: 'bg-orange-500/95 text-white', label: 'Low' },
  weak:        { bg: 'from-rose-500 to-rose-600', pill: 'bg-rose-500/95 text-white', label: 'Weak' },
};

const RANK_COLORS = ['#B98A54', '#9CA0A6', '#8C5B2D'];

export default function RentalLeaderboardPanel() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [totalRanked, setTotalRanked] = useState(0);
  const [minListings, setMinListings] = useState(5);
  const [propertyType, setPropertyType] = useState('');
  const [bedrooms, setBedrooms] = useState('');
  const [top, setTop] = useState(20);
  const [expandedIdx, setExpandedIdx] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          min_listings: String(minListings),
          top: String(top),
        });
        if (propertyType) params.set('property_type', propertyType);
        if (bedrooms) params.set('bedrooms', bedrooms);
        const { data } = await api.get(`/trends/rental-leaderboard?${params}`);
        if (cancelled) return;
        setRows(data.leaderboard || []);
        setTotalRanked(data.total_communities_ranked || 0);
      } catch {
        if (!cancelled) toast.error('Failed to load rental leaderboard.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [minListings, propertyType, bedrooms, top]);

  const podium = rows.slice(0, 3);
  const rest = rows.slice(3);

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-8" data-testid="rental-leaderboard-page">
      <div className="mb-8 flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#93704A] font-semibold mb-2">
            <Sparkles size={11}/> Iris Original · Live signal
          </div>
          <h1 className="font-serif text-4xl md:text-5xl text-slate-900 mb-2">Rental Hold Leaderboard</h1>
          <p className="text-sm text-slate-600 max-w-2xl">
            Dubai&apos;s top-yielding communities <span className="text-slate-900 font-medium">right now</span>. Gross yield = median live rent AED/sqft ÷ median live sale AED/sqft. Fresh signal — no 90-day DLD delay.
          </p>
        </div>
      </div>

      <div className="mb-8 flex flex-nowrap md:flex-wrap items-center gap-2 bg-white/60 backdrop-blur-sm p-3 rounded-2xl border border-white/70 overflow-x-auto no-scrollbar">
        <div className="flex items-center gap-1 text-xs text-slate-500 mr-2 flex-shrink-0"><Filter size={12}/> <span className="hidden sm:inline">Filter</span></div>
        <select value={propertyType} onChange={(e) => setPropertyType(e.target.value)}
          data-testid="leaderboard-property-type"
          className="px-3 py-1.5 rounded-full bg-white/85 border border-white/70 text-xs flex-shrink-0">
          <option value="">All types</option>
          <option value="apartment">Apartment</option>
          <option value="villa">Villa</option>
          <option value="townhouse">Townhouse</option>
          <option value="penthouse">Penthouse</option>
          <option value="studio">Studio</option>
          <option value="office">Office (commercial)</option>
          <option value="shop">Shop (commercial)</option>
          <option value="warehouse">Warehouse</option>
        </select>
        <select value={bedrooms} onChange={(e) => setBedrooms(e.target.value)}
          data-testid="leaderboard-bedrooms"
          className="px-3 py-1.5 rounded-full bg-white/85 border border-white/70 text-xs flex-shrink-0">
          <option value="">All beds</option>
          <option value="0">Studio</option>
          <option value="1">1 BR</option>
          <option value="2">2 BR</option>
          <option value="3">3 BR</option>
          <option value="4">4 BR</option>
          <option value="5">5+ BR</option>
        </select>
        <select value={minListings} onChange={(e) => setMinListings(Number(e.target.value))}
          data-testid="leaderboard-min-listings"
          className="px-3 py-1.5 rounded-full bg-white/85 border border-white/70 text-xs flex-shrink-0">
          <option value={3}>3+ live rents</option>
          <option value={5}>5+ live rents</option>
          <option value={10}>10+ live rents</option>
          <option value={25}>25+ live rents (highest confidence)</option>
        </select>
        <select value={top} onChange={(e) => setTop(Number(e.target.value))}
          data-testid="leaderboard-top"
          className="px-3 py-1.5 rounded-full bg-white/85 border border-white/70 text-xs flex-shrink-0">
          <option value={10}>Top 10</option>
          <option value={20}>Top 20</option>
          <option value={50}>Top 50</option>
        </select>
        <span className="ml-auto text-xs text-slate-500 flex-shrink-0 whitespace-nowrap" data-testid="leaderboard-total-ranked">
          {loading ? 'Loading…' : `${totalRanked} communities ranked`}
        </span>
      </div>

      {loading && rows.length === 0 && (
        <div className="grid place-items-center py-24 text-slate-400 text-sm">
          <Loader2 size={20} className="animate-spin mb-2 text-[#679286]"/>
          Analyzing live rents across 12,000+ listings…
        </div>
      )}

      {!loading && rows.length === 0 && (
        <div className="grid place-items-center py-24 text-slate-500 text-sm">
          <Info size={28} className="mb-3 text-slate-300"/>
          No communities meet the current filter. Try lowering &quot;min live rents&quot;.
        </div>
      )}

      {podium.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8" data-testid="leaderboard-podium">
          {podium.map((r, i) => {
            const style = BAND_STYLES[r.band] || BAND_STYLES.moderate;
            return (
              <div key={r.community}
                className={`relative rounded-3xl bg-gradient-to-br ${style.bg} p-6 text-white overflow-hidden shadow-lg`}
                data-testid={`leaderboard-podium-${i + 1}`}
              >
                <div className="absolute top-4 right-4 opacity-30">
                  <Trophy size={72} strokeWidth={1.5} style={{ color: RANK_COLORS[i] }}/>
                </div>
                <div className="relative">
                  <div className="text-[10px] uppercase tracking-widest opacity-80 mb-1">#{i + 1} · {style.label}</div>
                  <div className="font-serif text-2xl mb-3 leading-tight">{r.community}</div>
                  <div className="text-5xl font-serif font-bold mb-1">{r.gross_yield_pct}<span className="text-2xl">%</span></div>
                  <div className="text-xs opacity-90 mb-4">gross rental yield</div>
                  <div className="flex items-center gap-4 text-[11px] opacity-90 border-t border-white/20 pt-3">
                    <div>
                      <div className="opacity-70">Median annual rent</div>
                      <div className="font-medium text-sm">{r.median_annual_rent_aed ? AED_short(r.median_annual_rent_aed) : '—'}</div>
                    </div>
                    <div>
                      <div className="opacity-70">Sample</div>
                      <div className="font-medium text-sm">{r.rent_sample_count} rent · {r.sale_sample_count} sale</div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {rest.length > 0 && (
        <div className="rounded-2xl overflow-hidden border border-white/70 bg-white/60 backdrop-blur-sm" data-testid="leaderboard-table">
          <div className="hidden md:grid grid-cols-[52px_1fr_100px_120px_140px_120px_44px] items-center gap-2 px-4 py-2.5 bg-slate-100/70 text-[10px] uppercase tracking-widest text-slate-500 border-b border-white/70">
            <div>Rank</div><div>Community</div>
            <div className="text-right">Gross yield</div>
            <div className="text-right">Median rent/yr</div>
            <div className="text-right">Rent psf → Sale psf</div>
            <div className="text-right">Samples</div><div></div>
          </div>
          {rest.map((r, i) => {
            const rank = i + 4;
            const style = BAND_STYLES[r.band] || BAND_STYLES.moderate;
            const expanded = expandedIdx === rank;
            return (
              <div key={r.community} data-testid={`leaderboard-row-${rank}`}>
                <button
                  onClick={() => setExpandedIdx(expanded ? null : rank)}
                  className={`w-full md:grid md:grid-cols-[52px_1fr_100px_120px_140px_120px_44px] flex items-center gap-2 px-4 py-3 text-sm border-b border-white/60 hover:bg-white/80 transition text-left ${expanded ? 'bg-white/80' : ''}`}
                >
                  <div className="font-serif text-slate-500 text-base">#{rank}</div>
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <div className={`w-1.5 h-8 rounded-full bg-gradient-to-b ${style.bg}`}/>
                    <div className="min-w-0">
                      <div className="font-medium text-slate-900 truncate">{r.community}</div>
                      <div className="text-[10px] text-slate-500">{r.emirate}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${style.pill}`}>{r.gross_yield_pct}%</span>
                  </div>
                  <div className="text-right font-medium text-slate-800 text-xs hidden md:block">
                    {r.median_annual_rent_aed ? AED_short(r.median_annual_rent_aed) : '—'}
                  </div>
                  <div className="text-right text-[11px] text-slate-600 font-mono hidden md:block">
                    {r.median_rent_psf} → {r.median_sale_psf}
                  </div>
                  <div className="text-right text-[11px] text-slate-500 hidden md:block">
                    {r.rent_sample_count}r · {r.sale_sample_count}s
                  </div>
                  <div className="hidden md:block">
                    <ChevronRight size={14} className={`text-slate-400 transition-transform ${expanded ? 'rotate-90' : ''}`}/>
                  </div>
                </button>
                {expanded && (
                  <div className="px-4 py-4 bg-white/40 border-b border-white/60">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link to={`/app/market/live?community=${encodeURIComponent(r.community)}&type=rent`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-sky-500/90 text-white text-xs hover:bg-sky-500"
                        data-testid={`leaderboard-view-rent-${rank}`}>
                        <Home size={11}/> See {r.rent_sample_count} rentals
                      </Link>
                      <Link to={`/app/market/live?community=${encodeURIComponent(r.community)}&type=sale`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-xs hover:bg-slate-50 text-slate-700"
                        data-testid={`leaderboard-view-sale-${rank}`}>
                        <Building2 size={11}/> See {r.sale_sample_count} for-sale
                      </Link>
                      <Link to={`/app/market?community=${encodeURIComponent(r.community)}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-xs hover:bg-slate-50 text-slate-700">
                        <TrendingUp size={11}/> Community trends <ArrowUpRight size={9}/>
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-8 p-4 rounded-2xl bg-white/40 border border-white/60 text-xs text-slate-600 leading-relaxed">
        <div className="font-medium text-slate-800 mb-1 flex items-center gap-1.5">
          <Info size={12}/> Methodology
        </div>
        <p>
          <strong>Gross rental yield =</strong> median live asking rent AED/sqft ÷ median live asking sale AED/sqft × 100. We blend DLD-sold psf (last 90 days) with live sale asks to stabilize the sale denominator.
          Communities need at least <strong>{minListings} live rent listings</strong> AND 2+ sale samples to appear. Yields above 20% are treated as data anomalies and clipped.
          Data refreshes every 6h scheduled, or on-demand from Live Listings.
        </p>
      </div>
    </div>
  );
}
